import { useState, useMemo, useCallback, useEffect } from "react";
import {
	Row,
	Col,
	Card,
	Skeleton,
	Empty,
	Image,
	Button,
	Pagination,
	Typography,
	Select,
	Input,
	Table,
	Switch,
	Flex,
	Menu,
	Dropdown,
} from "antd";
import {
	EditOutlined,
	ReloadOutlined,
	AppstoreOutlined,
	TableOutlined,
	LineChartOutlined,
	DownOutlined,
} from "@ant-design/icons";
import debounce from "lodash.debounce";
import { Link, useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import PrimaryButton from "./Buttons/PrimaryButton";
import CustomTable from "./Table/CustomTable";

const { Title, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;

const PAGE_SIZE = 12; // Number of products per page

const ShopifyProducts = ({
	products,
	openModal,
	refreshProducts,
	onSearch,
	onSort,
	fetchProducts,
	pageTitle = "Shopify Products",
	openSEOModal,
}) => {
	const [currentPage, setCurrentPage] = useState(1);
	const [sortOrder, setSortOrder] = useState("asc");
	const [viewMode, setViewMode] = useState("table"); // Toggle between 'card' and 'table'
	const { userData } = useAuth();

	const navigate = useNavigate();

	// Debounced search to prevent excessive re-renders
	const debouncedSearch = useMemo(
		() =>
			debounce((value) => {
				if (onSearch) onSearch(value);
			}, 300),
		[onSearch]
	);

	// Paginate products using useMemo
	const paginatedProducts = useMemo(() => {
		console.log(currentPage);
		const startIndex = (currentPage - 1) * PAGE_SIZE;
		return products?.slice(startIndex, startIndex + PAGE_SIZE) || [];
	}, [products, currentPage]);

	useEffect(() => {
		setCurrentPage(1); // Reset to the first page
	}, [products]);

	// Handle search input change
	const handleSearch = (e) => debouncedSearch(e?.target?.value);

	// Handle sort order change
	const handleSort = (order) => {
		setSortOrder(order);
		if (onSort) onSort(order);
	};

	// Pagination change handler using useCallback to prevent re-renders
	const handlePageChange = useCallback((page) => setCurrentPage(page), []);

	// Define table columns
	const columns = [
		{
			title: "Title",
			dataIndex: "title",
			key: "title",
			width: "100px",
			render: (_, record) => (
				<Image
					src={record?.images?.edges?.[0]?.node?.originalSrc || ""}
					alt={record?.title || "Product Image"}
					style={{ height: 50, objectFit: "cover" }}
					fallback="https://via.placeholder.com/200"
				/>
			),
		},
		{
			title: "Title",
			dataIndex: "title",
			key: "title",
			width: "250px",
			render: (text) => (
				<Title level={5} style={{ minWidth: "150px", margin: "0" }}>
					{text}
				</Title>
			),
		},
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
			render: (text) => (
				<Paragraph ellipsis={{ rows: 2 }}>
					{text || "No description available."}
				</Paragraph>
			),
		},
		{
			title: "Actions",
			key: "actions",
			width: "150px",
			render: (_, record) => (
				<Dropdown overlay={<ActionMenu record={record} />} trigger={["click"]}>
					<PrimaryButton height={"30px"}>
						Actions <DownOutlined />
					</PrimaryButton>
				</Dropdown>
			),
		},
	];
	const ActionMenu = ({ record }) => {
		return (
			<Menu>
				<Menu.Item
					key="generate-ai-description"
					onClick={() => {
						const extractedId = record?.id?.replace(
							"gid://shopify/Product/",
							""
						);
						navigate(`/products/generate-description/${extractedId}`);
					}}
				>
					Generate AI Description
				</Menu.Item>
				<Menu.Item key="seo-optimizer" onClick={() => openSEOModal(record)}>
					SEO Optimizer
				</Menu.Item>
				<Link
					to={`https://${userData?.shopName}/products/${record?.handle}`}
					target="_blank"
				>
					<Menu.Item key="preview">Visit Product</Menu.Item>
				</Link>
			</Menu>
		);
	};

	return (
		<div>
			{/* Header Row */}
			<Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
				<Title level={2} style={{ margin: "0px" }}>
					{pageTitle}
				</Title>

				<div
					style={{
						display: "flex",
						gap: 10,
						flexWrap: "wrap",
						alignItems: "center",
					}}
				>
					<Search
						placeholder="Search products..."
						allowClear
						onChange={handleSearch}
						style={{ width: 200 }}
					/>
					<Select
						defaultValue="asc"
						style={{ width: 120 }}
						value={sortOrder}
						onChange={handleSort}
					>
						<Option value="asc">Sort A-Z</Option>
						<Option value="desc">Sort Z-A</Option>
					</Select>
					<Button
						type="default"
						icon={<ReloadOutlined />}
						onClick={fetchProducts}
					>
						Refresh
					</Button>
					{/* Toggle Button for Card / Table View */}
					<Switch
						checkedChildren={<TableOutlined />}
						unCheckedChildren={<AppstoreOutlined />}
						checked={viewMode === "table"}
						onChange={() => setViewMode(viewMode === "card" ? "table" : "card")}
					/>
				</div>
			</Row>

			{/* Table View */}
			{viewMode === "table" ? (
				<Col
					span={24}
					style={{
						width: "100%",
						overflow: "hidden",
						overflowX: "auto",
					}}
				>
					<CustomTable
						columns={columns}
						dataSource={paginatedProducts}
						loading={products?.length > -1 ? false : true}
						pagination={{
							current: currentPage,
							pageSize: PAGE_SIZE,
							total: products?.length,
							onChange: handlePageChange,
						}}
					/>
				</Col>
			) : (
				// Card View
				<Row gutter={[16, 16]}>
					{!products ? (
						// Skeleton Loading Placeholder
						Array.from({ length: PAGE_SIZE }).map((_, index) => (
							<Col xs={24} sm={12} md={8} lg={6} key={index}>
								<Card hoverable>
									<Skeleton active paragraph={{ rows: 2 }} />
									<Skeleton.Button active style={{ width: "100%" }} />
								</Card>
							</Col>
						))
					) : products.length === 0 ? (
						<Col span={24} style={{ textAlign: "center", marginTop: 50 }}>
							<Empty description="No products available" />
						</Col>
					) : (
						// Render paginated products
						paginatedProducts.map((product, index) => {
							const imageSrc =
								product?.images?.edges?.[0]?.node?.originalSrc || "";
							return (
								<Col xs={24} sm={12} md={8} lg={6} key={product.id || index}>
									<Card
										hoverable
										cover={
											<Image
												src={imageSrc}
												alt={product?.title || "Product Image"}
												style={{ height: 200, objectFit: "cover" }}
												preview={false}
												fallback="https://via.placeholder.com/200"
											/>
										}
										actions={[
											<Flex
												style={{
													flexDirection: "column",
													gap: "12px",
												}}
											>
												<Col xs={24} style={{ padding: "0px 10px" }}>
													<Button
														type="primary"
														icon={<EditOutlined />}
														onClick={() => openModal(product)}
														style={{
															width: "100%",
															borderRadius: "8px",
															background:
																"linear-gradient(90deg, #6D5BBA, #8D58BF)",
															border: "none",
															fontWeight: 500,
															height: 40,
														}}
													>
														Generate AI Description
													</Button>
												</Col>
												<Col xs={24} style={{ padding: "0px 10px" }}>
													<Button
														type="primary"
														icon={<LineChartOutlined />}
														onClick={() => openSEOModal(product)}
														style={{
															width: "100%",
															borderRadius: "8px",
															background:
																"linear-gradient(90deg, #6D5BBA, #8D58BF)",
															border: "none",
															fontWeight: 500,
															height: 40,
														}}
													>
														SEO Optimizer
													</Button>
												</Col>
											</Flex>,
										]}
									>
										<Card.Meta
											title={<Title level={4}>{product?.title}</Title>}
											description={
												<Paragraph ellipsis={{ rows: 3 }}>
													{product?.description || "No description available."}
												</Paragraph>
											}
										/>
									</Card>
								</Col>
							);
						})
					)}
				</Row>
			)}

			{/* Pagination */}
			{products && products.length > PAGE_SIZE && viewMode === "card" && (
				<Row justify="center" style={{ marginTop: 20 }}>
					<Pagination
						current={currentPage}
						total={products.length}
						pageSize={PAGE_SIZE}
						onChange={handlePageChange}
						showSizeChanger={false}
					/>
				</Row>
			)}
		</div>
	);
};

export default ShopifyProducts;
